import {
  Button,
  Icon,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "@salestrak/ui";

import type { IssueRow } from "#app/lib/data";
import { useIssueRenameOptional } from "#app/components/issues/issue-rename-context";
import type { Omittable } from "@salestrak/ui";

/**
 * The hover Rename pencil: pressing it puts the issue's own name text into
 * edit mode in place, through the hosting view's IssueRenameControls. It
 * reads the rename context ITSELF (never through a column def or card prop —
 * the flexRender remount trap), so defs and cards stay referentially stable
 * across rename start/end. Without a provider, or on an issue the viewer
 * cannot edit, it renders nothing.
 *
 * `variant` is the host's chrome: the table row's opaque outline chip
 * (squared by the row's own descendant rule) vs the kanban card cluster's
 * pill-shaped ghost action — both on the 28px `icon-sm` rung.
 */
export function IssueRenameAction({
  issue,
  variant = "outline",
}: Omittable<{
  issue: IssueRow;
  variant?: "outline" | "ghost";
}>) {
  const rename = useIssueRenameOptional();
  if (!rename || !issue.viewerCanEdit) return null;
  // While THIS issue's title is in inline edit, the pencil is the button that
  // launched the edit — showing it on top of the live field is redundant, so
  // it retires until the edit commits or cancels.
  if (rename.renamingIssueId === issue.issueId) return null;
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant={variant}
            size="icon-sm"
            shape={variant === "ghost" ? "pill" : undefined}
            aria-label={`Rename ${issue.name}`}
            onClick={(e: React.MouseEvent) => {
              // The hosting cell/card opens the project on click; the action
              // press must not also do that.
              e.stopPropagation();
              rename.onRenameStart(issue.issueId);
            }}
          >
            <Icon name="pencil" color="muted" />
          </Button>
        }
      />
      <TooltipPopup side="top">Rename</TooltipPopup>
    </Tooltip>
  );
}
